import React, { useEffect, useState, useCallback } from "react";
import { Bell, BellRing, RefreshCw, BellOff, ShieldAlert } from "lucide-react";
import * as api from "../../api";

/**
 * ReopenNotifyButton — public "Avisarme al abrir" button.
 *
 * Shown inside the site-closed banner. Runs the full Web Push ceremony
 * (permission → SW registration → PushManager subscribe → POST /push/waitlist)
 * so the visitor gets ONE native push notification the moment the admin flips
 * the store back to open.
 *
 * State is persisted in localStorage so the button remembers the user opted
 * in across reloads while the store is still closed.
 */

const LS_KEY = "ag7_reopen_waitlist_endpoint";

function b64UrlToUint8Array(b64) {
  const pad = "=".repeat((4 - (b64.length % 4)) % 4);
  const base64 = (b64 + pad).replace(/-/g, "+").replace(/_/g, "/");
  const raw = atob(base64);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
  return out;
}

export function ReopenNotifyButton({ showToast }) {
  const [supported, setSupported] = useState(true);
  const [busy, setBusy] = useState(false);
  const [subscribed, setSubscribed] = useState(false);
  const [permission, setPermission] = useState(
    typeof Notification !== "undefined" ? Notification.permission : "default",
  );

  useEffect(() => {
    if (typeof window === "undefined") return;
    const ok =
      "serviceWorker" in navigator &&
      "PushManager" in window &&
      "Notification" in window;
    setSupported(ok);
    if (!ok) return;
    // If we still have an active subscription tied to the reopen waitlist,
    // reflect that in the UI so the user doesn't re-tap.
    (async () => {
      try {
        const savedEndpoint = localStorage.getItem(LS_KEY);
        if (!savedEndpoint) return;
        const reg = await navigator.serviceWorker.getRegistration("/sw.js");
        const sub = reg ? await reg.pushManager.getSubscription() : null;
        if (sub && sub.endpoint === savedEndpoint) setSubscribed(true);
        else localStorage.removeItem(LS_KEY);
      } catch (e) {
        console.warn("ReopenNotifyButton: status check failed:", e);
      }
    })();
  }, []);

  const enable = useCallback(async () => {
    if (busy || subscribed) return;
    setBusy(true);
    try {
      const perm = await Notification.requestPermission();
      setPermission(perm);
      if (perm !== "granted") {
        showToast?.(
          "Permiso denegado",
          "Actívalas en la configuración del navegador para que podamos avisarte.",
          "error",
        );
        return;
      }
      const { public_key, enabled } = await api.getPushVapidKey();
      if (!enabled || !public_key)
        throw new Error("El servidor no tiene VAPID configurado.");

      const reg = await navigator.serviceWorker.register("/sw.js");
      await navigator.serviceWorker.ready;

      let sub = await reg.pushManager.getSubscription();
      if (!sub) {
        sub = await reg.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: b64UrlToUint8Array(public_key),
        });
      }
      const raw = sub.toJSON();
      await api.pushWaitlistSubscribe({
        endpoint: raw.endpoint,
        keys: raw.keys,
        user_agent: navigator.userAgent || "",
      });
      try {
        localStorage.setItem(LS_KEY, raw.endpoint);
      } catch (e) {
        console.warn("ReopenNotifyButton: persist endpoint failed:", e);
      }
      setSubscribed(true);
      showToast?.(
        "¡Listo!",
        "Te avisaremos con una notificación en cuanto la tienda vuelva a abrir.",
        "success",
      );
    } catch (err) {
      showToast?.(
        "No pudimos activar la alerta",
        err.message || String(err),
        "error",
      );
    } finally {
      setBusy(false);
    }
  }, [busy, subscribed, showToast]);

  if (!supported) {
    return (
      <div
        data-testid="reopen-notify-unsupported"
        className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl text-[11px] font-bold text-amber-300 border border-amber-500/40 bg-amber-500/10"
      >
        <ShieldAlert className="w-3.5 h-3.5" />
        <span>Tu navegador no soporta notificaciones push.</span>
      </div>
    );
  }

  if (permission === "denied") {
    return (
      <div
        data-testid="reopen-notify-denied"
        className="inline-flex items-center gap-2 px-3 py-2 rounded-xl text-[11px] font-bold text-red-300 border border-red-500/40 bg-red-500/10"
      >
        <BellOff className="w-3.5 h-3.5" />
        <span>Permiso bloqueado. Habilítalo desde el navegador para recibir el aviso.</span>
      </div>
    );
  }

  return (
    <button
      onClick={enable}
      disabled={busy || subscribed}
      data-testid={subscribed ? "reopen-notify-subscribed-btn" : "reopen-notify-enable-btn"}
      aria-pressed={subscribed}
      className={`group relative inline-flex items-center gap-2 px-5 py-3 rounded-2xl text-sm font-black uppercase tracking-widest transition-all disabled:cursor-default ${
        subscribed
          ? "bg-emerald-500/15 text-emerald-200 border-2 border-emerald-400/60 shadow-[0_0_25px_-6px_rgba(16,185,129,0.7)]"
          : "bg-cyan-500/15 text-cyan-100 border-2 border-cyan-400/60 hover:bg-cyan-500/25 shadow-[0_0_25px_-6px_rgba(34,211,238,0.7)] hover:shadow-[0_0_35px_-4px_rgba(34,211,238,0.85)]"
      }`}
    >
      {busy ? (
        <RefreshCw className="w-4 h-4 animate-spin" />
      ) : subscribed ? (
        <BellRing className="w-4 h-4" />
      ) : (
        <Bell className="w-4 h-4 group-hover:animate-pulse" />
      )}
      <span data-testid="reopen-notify-btn-label">
        {subscribed ? "¡Te avisaremos!" : "Avisarme al abrir"}
      </span>
    </button>
  );
}

export default ReopenNotifyButton;
